/**
 * 部屋・お知らせの画面で使う言葉（運用される方が読む名前）。
 *
 * 値そのものの定義元はサーバ（`server/src/channels/presets.ts`）。ここはその**言い換え**だけ。
 * 表に無い種類が来ても画面は壊さず、受け取った値をそのまま出す（設定の表で種類を足せるため・
 * docs/01 7-1）。
 */

/** 種類の言い換え。設定の表で足した種類は、キーをそのまま出す。 */
const CHANNEL_TYPE_LABELS: Record<string, string> = {
  official: '公式',
  group: 'グループ',
  dm: '1対1',
  announcement: 'お知らせ',
  teach: 'AI先輩に教える',
};

export function channelTypeLabel(type: string): string {
  return CHANNEL_TYPE_LABELS[type] ?? type;
}

/** 種類の1行の説明（作るときに選びやすいように）。 */
const CHANNEL_TYPE_NOTES: Record<string, string> = {
  official: '拠点・部署の連絡用のトークです。参加している方は、どなたでもメッセージを送れます。',
  group: '現場のふだんのやりとりに使うトークです。',
  announcement: '全社・拠点への一斉連絡用のトークです。メッセージを送れるのは管理者だけです。',
  teach: 'AI先輩に覚えてもらいたいことを書くトークです。',
};

export function channelTypeNote(type: string): string {
  return CHANNEL_TYPE_NOTES[type] ?? '';
}

/**
 * **削除できる種類**（docs/01 7-9・サーバの `GROUP_CHANNEL_TYPE`）。
 *
 * いまのところグループだけです。1対1・公式・お知らせの畳み方には別の決めごとが要ります。
 * ここで出し分けているのは**ボタンの見せ方だけ**で、できる・できないはサーバが決めます。
 */
export const DELETABLE_CHANNEL_TYPE = 'group';

/** 1対1の部屋の種類（「投稿できる方」を選べない・決定27）。 */
export const DIRECT_CHANNEL_TYPE = 'dm';

/**
 * 「AI先輩に教える」部屋の種類の名前。どなたでも投稿できる部屋なので、
 * 投稿できる方の設定は画面でも変えられないようにする（決定23・issue #256。サーバでも断る）。
 */
export const TEACH_CHANNEL_TYPE = 'teach';

/** 保存日数の言い方（空＝無期限）。 */
export function retentionLabel(days: number | null): string {
  return days === null ? '無期限' : `${days.toLocaleString('ja-JP')}日`;
}

/** 拠点の言い方（拠点を持たない部屋＝全社）。 */
export function channelSiteLabel(siteName: string | null): string {
  return siteName ?? '全社';
}

/** 投稿できる範囲の言い方。 */
export function postPolicyLabel(policy: 'members' | 'admins'): string {
  return policy === 'admins' ? '管理者のみ' : '参加している方';
}

/** 既読の見せ方の言い方。 */
export function readReceiptLabel(display: 'count' | 'none'): string {
  return display === 'count' ? '既読の人数を出す' : '既読を出さない';
}

/**
 * 日時の言い方（読めない値が来ても画面を壊さない）。形は全画面で同じ（format/datetime.ts）。
 * 値が無いとき（まだメッセージがない・古いサーバが項目を返さない）は、空白にせず「―」と出す。
 */
export { formatDateTime } from '../format/datetime.js';
